import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Minus, Plus, ShoppingBag, ChevronRight, Truck, Check, Store, Package, FileText } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { categories } from "../mock";
import ProductCard from "../components/ProductCard";
import API from "../api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";

const ProductDetail = () => {
  const { id } = useParams();
  const { t, tl, addToCart } = useApp();
  const { user } = useAuth();
  const { toast } = useToast();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [flavor, setFlavor] = useState(null);
  const [qty, setQty] = useState(1);
  const [sizeError, setSizeError] = useState(false);
  const [flavorError, setFlavorError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [ageOpen, setAgeOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ company: "", contact_name: "", email: "", phone: "", quantity: "", message: "" });
  const [quoteSending, setQuoteSending] = useState(false);

  useEffect(() => {
    setNotFound(false); setSize(null); setQty(1); setFlavor(null); setFlavorError(false);
    API.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setColor(r.data.colors?.[0] || null);
      API.get(`/products?category=${r.data.category}`).then((rr) => setRelated(rr.data.filter((p) => p.id !== r.data.id).slice(0, 4))).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 text-center">
        <p className="text-muted-foreground">Produit introuvable.</p>
        <Link to="/" className="text-[#b08d57] mt-4 inline-block">{t("home")}</Link>
      </div>
    );
  }
  if (!product) return <div className="max-w-[1400px] mx-auto px-4 py-24 text-center text-muted-foreground">…</div>;

  const hasSizes = product.sizes?.length > 0;
  const hasFlavors = product.flavors?.length > 0;
  const discount = product.oldPrice ? Math.round((1 - product.price / product.oldPrice) * 100) : 0;
  const isB2B = product.category === "negoce";
  const isRestricted = product.category === "vape" || product.category === "drinks";
  const isVape = product.category === "vape";
  const canBuyB2B = user && (user.pro_status === "approved" || user.role === "admin");

  const doAdd = () => {
    addToCart(product, size, color, qty, hasFlavors ? tl(flavor) : null);
    toast({ title: t("addedToCart"), description: `${tl(product.name)} — ${product.brand}` });
  };

  const handleAdd = () => {
    if (hasSizes && !size) { setSizeError(true); return; }
    if (hasFlavors && !flavor) { setFlavorError(true); return; }
    if (isRestricted && localStorage.getItem("adl_age_ok") !== "1") { setAgeOpen(true); return; }
    doAdd();
  };

  const confirmAge = () => {
    localStorage.setItem("adl_age_ok", "1");
    setAgeOpen(false);
    doAdd();
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    setQuoteSending(true);
    try {
      await API.post("/quotes", {
        product_id: product.id,
        product_name: tl(product.name),
        company: quoteForm.company,
        contact_name: quoteForm.contact_name,
        email: quoteForm.email,
        phone: quoteForm.phone || null,
        quantity: parseInt(quoteForm.quantity, 10) || product.moq || 1,
        message: quoteForm.message || null,
      });
      setQuoteOpen(false);
      setQuoteForm({ company: "", contact_name: "", email: "", phone: "", quantity: "", message: "" });
      toast({ title: t("quoteSent") });
    } catch (err) {
      toast({ title: "Erreur", description: err.response?.data?.detail || "Échec de l'envoi." });
    } finally {
      setQuoteSending(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 fade-up">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-6">
        <Link to="/" className="hover:text-[#b08d57]">{t("home")}</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/c/${product.category}`} className="hover:text-[#b08d57]">{t(categories.find((c) => c.id === product.category)?.key)}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-[#1b1b1b] line-clamp-1">{tl(product.name)}</span>
      </div>
      <div className="grid md:grid-cols-2 gap-8 lg:gap-14">
        <div className="relative overflow-hidden rounded-sm bg-white aspect-[3/4] md:sticky md:top-28 self-start">
          <img src={product.image} alt={tl(product.name)} className="w-full h-full object-cover" />
          {discount > 0 && (<span className="absolute top-4 left-4 bg-[#8b1e3f] text-white text-xs tracking-wide px-3 py-1.5">-{discount}%</span>)}
        </div>
        <div className="max-w-lg">
          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{product.brand}</span>
          <h1 className="font-serif-lux text-3xl md:text-4xl mt-2 leading-tight text-[#1b1b1b]">{tl(product.name)}</h1>
          {product.vendor_name && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2"><Store className="h-3.5 w-3.5" strokeWidth={1.5} /> {product.vendor_name}</p>
          )}
          <div className="flex items-center gap-2 mt-3">
            <div className="flex items-center gap-0.5">{[1, 2, 3, 4, 5].map((s) => (<Star key={s} className={`h-4 w-4 ${s <= Math.round(product.rating) ? "fill-[#b08d57] text-[#b08d57]" : "text-gray-300"}`} />))}</div>
            <span className="text-sm text-muted-foreground">{product.rating} · {product.reviews} {t("reviews")}</span>
          </div>
          <div className="flex items-center gap-3 mt-5">
            {isB2B && <span className="text-sm text-muted-foreground">{t("fromPrice")}</span>}
            <span className="text-2xl font-medium text-[#1b1b1b]">{product.price} €{isB2B && <span className="text-sm font-normal text-muted-foreground"> {t("perUnit")}</span>}</span>
            {product.oldPrice && <span className="text-lg text-muted-foreground line-through">{product.oldPrice} €</span>}
          </div>
          {isB2B && product.moq && (
            <div className="flex items-center gap-2 mt-4 text-sm text-[#1b1b1b]" data-testid="b2b-moq">
              <Package className="h-4 w-4 text-[#b08d57]" strokeWidth={1.5} />
              {t("moqLabel")} : <span className="font-medium">{product.moq} {product.unit ? tl(product.unit) : ""}</span>
            </div>
          )}
          <p className="text-[15px] text-[#444] leading-relaxed mt-5">{tl(product.desc)}</p>
          {isB2B && product.tiers?.length > 0 && (
            <div className="mt-7 border border-border rounded-sm overflow-hidden" data-testid="b2b-tiers">
              <div className="bg-[#f6f2ec] px-4 py-2.5 text-sm text-[#1b1b1b] tracking-wide">{t("tieredPricing")}</div>
              <table className="w-full text-sm">
                <thead><tr className="text-muted-foreground border-b border-border"><th className="text-left font-normal px-4 py-2">{t("tierQty")}</th><th className="text-right font-normal px-4 py-2">{t("tierPrice")}</th></tr></thead>
                <tbody>
                  {product.tiers.map((tier, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5">≥ {tier.qty} {product.unit ? tl(product.unit) : ""}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-[#1b1b1b]">{tier.price} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {product.colors?.length > 0 && (
            <div className="mt-7">
              <span className="text-sm text-[#1b1b1b]">{t("color")}</span>
              <div className="flex gap-2.5 mt-2.5">{product.colors.map((c, i) => (<button key={i} onClick={() => setColor(c)} className={`h-8 w-8 rounded-full border-2 transition-all ${color === c ? "border-[#b08d57] scale-110" : "border-black/10"}`} style={{ backgroundColor: c }} aria-label={`color-${i}`} />))}</div>
            </div>
          )}
          {hasSizes && (
            <div className="mt-7">
              <div className="flex items-center justify-between"><span className="text-sm text-[#1b1b1b]">{isVape ? t("nicotineLabel") : t("size")}</span>{sizeError && <span className="text-xs text-[#8b1e3f]">{t("selectSize")}</span>}</div>
              <div className="flex flex-wrap gap-2 mt-2.5">{product.sizes.map((s) => (<button key={s} onClick={() => { setSize(s); setSizeError(false); }} className={`min-w-[52px] px-3 py-2.5 text-sm border transition-all ${size === s ? "border-[#1b1b1b] bg-[#1b1b1b] text-white" : "border-border hover:border-[#1b1b1b]"}`}>{s}</button>))}</div>
            </div>
          )}
          {hasFlavors && (
            <div className="mt-7" data-testid="flavor-selector">
              <div className="flex items-center justify-between"><span className="text-sm text-[#1b1b1b]">{t("flavor")}</span>{flavorError && <span className="text-xs text-[#8b1e3f]">{t("selectFlavor")}</span>}</div>
              <div className="flex flex-wrap gap-2 mt-2.5">{product.flavors.map((f, i) => { const active = flavor && tl(flavor) === tl(f); return (<button key={i} onClick={() => { setFlavor(f); setFlavorError(false); }} className={`px-4 py-2.5 text-sm border transition-all ${active ? "border-[#1b1b1b] bg-[#1b1b1b] text-white" : "border-border hover:border-[#1b1b1b]"}`}>{tl(f)}</button>); })}</div>
            </div>
          )}
          {isRestricted && (
            <p className="mt-6 text-xs text-[#8b1e3f] bg-[#8b1e3f]/5 border border-[#8b1e3f]/20 rounded-sm px-3 py-2.5" data-testid="restricted-note">{t("restrictedNote")}</p>
          )}
          {isB2B ? (
            <div className="mt-8 space-y-3">
              <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
                <DialogTrigger asChild>
                  <button data-testid="request-quote-btn" className="w-full bg-[#1b1b1b] text-white py-3.5 text-sm tracking-[0.1em] uppercase flex items-center justify-center gap-2 hover:bg-[#b08d57] transition-colors"><FileText className="h-4 w-4" strokeWidth={1.5} /> {t("requestQuote")}</button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-serif-lux text-2xl">{t("quoteTitle")}</DialogTitle>
                    <DialogDescription className="text-sm">{t("quoteSubtitle")}</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={submitQuote} className="space-y-3 mt-2" data-testid="quote-form">
                    <div><Label className="text-xs">{t("companyLabel")}</Label><Input required data-testid="quote-company" value={quoteForm.company} onChange={(e) => setQuoteForm({ ...quoteForm, company: e.target.value })} className="mt-1" /></div>
                    <div><Label className="text-xs">{t("contactLabel")}</Label><Input required data-testid="quote-contact" value={quoteForm.contact_name} onChange={(e) => setQuoteForm({ ...quoteForm, contact_name: e.target.value })} className="mt-1" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">{t("emailLabel")}</Label><Input required type="email" data-testid="quote-email" value={quoteForm.email} onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })} className="mt-1" /></div>
                      <div><Label className="text-xs">{t("phoneLabel")}</Label><Input data-testid="quote-phone" value={quoteForm.phone} onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })} className="mt-1" /></div>
                    </div>
                    <div><Label className="text-xs">{t("desiredQty")}</Label><Input required type="number" min={product.moq || 1} placeholder={product.moq ? `${t("moqLabel")} : ${product.moq}` : ""} data-testid="quote-qty" value={quoteForm.quantity} onChange={(e) => setQuoteForm({ ...quoteForm, quantity: e.target.value })} className="mt-1" /></div>
                    <div><Label className="text-xs">{t("messageLabel")}</Label><Textarea data-testid="quote-message" value={quoteForm.message} onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })} className="mt-1" rows={3} /></div>
                    <button type="submit" disabled={quoteSending} data-testid="quote-submit" className="w-full bg-[#1b1b1b] text-white py-3 text-sm tracking-[0.1em] uppercase hover:bg-[#b08d57] transition-colors disabled:opacity-60">{t("sendQuote")}</button>
                  </form>
                </DialogContent>
              </Dialog>
              <div className="flex items-stretch gap-3">
                <div className="flex items-center border border-border rounded-sm">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3.5 hover:text-[#b08d57]" aria-label="minus"><Minus className="h-4 w-4" strokeWidth={1.5} /></button>
                  <span className="w-8 text-center text-sm">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="px-3.5 hover:text-[#b08d57]" aria-label="plus"><Plus className="h-4 w-4" strokeWidth={1.5} /></button>
                </div>
                {canBuyB2B ? (
                  <button onClick={handleAdd} data-testid="add-to-cart" className="flex-1 border border-[#1b1b1b] text-[#1b1b1b] py-3.5 text-sm tracking-[0.1em] uppercase flex items-center justify-center gap-2 hover:bg-[#1b1b1b] hover:text-white transition-colors"><ShoppingBag className="h-4 w-4" strokeWidth={1.5} /> {t("addToCart")}</button>
                ) : (
                  <div className="flex-1 flex items-center border border-dashed border-border rounded-sm px-3 text-xs text-muted-foreground" data-testid="b2b-buy-locked">{t("proReserved")}</div>
                )}
              </div>
              {!canBuyB2B && (
                <Link to="/auth?mode=register" className="inline-block text-sm text-[#b08d57] underline" data-testid="become-pro-link">{t("becomePro")}</Link>
              )}
            </div>
          ) : (
          <div className="flex items-stretch gap-3 mt-8">
            <div className="flex items-center border border-border rounded-sm">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3.5 hover:text-[#b08d57]" aria-label="minus"><Minus className="h-4 w-4" strokeWidth={1.5} /></button>
              <span className="w-8 text-center text-sm">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3.5 hover:text-[#b08d57]" aria-label="plus"><Plus className="h-4 w-4" strokeWidth={1.5} /></button>
            </div>
            <button onClick={handleAdd} data-testid="add-to-cart" className="flex-1 bg-[#1b1b1b] text-white py-3.5 text-sm tracking-[0.1em] uppercase flex items-center justify-center gap-2 hover:bg-[#b08d57] transition-colors"><ShoppingBag className="h-4 w-4" strokeWidth={1.5} /> {t("addToCart")}</button>
          </div>
          )}
          <div className="mt-7 space-y-2.5">
            <div className="flex items-center gap-2.5 text-sm text-[#444]"><Truck className="h-4 w-4 text-[#b08d57]" strokeWidth={1.5} /> {t("deliveryInfo")}</div>
            <div className="flex items-center gap-2.5 text-sm text-[#444]"><Check className="h-4 w-4 text-[#b08d57]" strokeWidth={1.5} /> Authenticité garantie</div>
          </div>
          <Accordion type="single" collapsible className="mt-8">
            <AccordionItem value="desc"><AccordionTrigger className="text-sm tracking-wide">{t("description")}</AccordionTrigger><AccordionContent className="text-sm text-[#555] leading-relaxed">{tl(product.desc)}</AccordionContent></AccordionItem>
            <AccordionItem value="details"><AccordionTrigger className="text-sm tracking-wide">{t("details")}</AccordionTrigger><AccordionContent className="text-sm text-[#555] leading-relaxed">Matières nobles sélectionnées avec soin. Nettoyage à sec recommandé. Fabriqué en Europe.</AccordionContent></AccordionItem>
            <AccordionItem value="delivery"><AccordionTrigger className="text-sm tracking-wide">{t("delivery")}</AccordionTrigger><AccordionContent className="text-sm text-[#555] leading-relaxed">{t("deliveryInfo")}</AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>
      {related.length > 0 && (
        <section className="mt-20">
          <div className="h-px w-10 bg-[#b08d57] mb-4" />
          <h2 className="font-serif-lux text-3xl text-[#1b1b1b] mb-8">{t("relatedTitle")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8">{related.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
        </section>
      )}
      <Dialog open={ageOpen} onOpenChange={setAgeOpen}>
        <DialogContent className="bg-white max-w-sm" data-testid="age-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-lux text-2xl">{t("ageTitle")}</DialogTitle>
            <DialogDescription className="text-sm">{t("ageQuestion")}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-2">
            <button onClick={confirmAge} data-testid="age-confirm" className="flex-1 bg-[#1b1b1b] text-white py-3 text-sm tracking-wide hover:bg-[#b08d57] transition-colors">{t("ageYes")}</button>
            <button onClick={() => setAgeOpen(false)} data-testid="age-deny" className="flex-1 border border-border py-3 text-sm tracking-wide hover:border-[#1b1b1b] transition-colors">{t("ageNo")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductDetail;
